/**
 * Chat haptic helper — guards all haptic calls behind the chatVibration setting.
 * Sound calls (sound.pop) always fire — sound has its own isMuted() check.
 */

import { haptic } from '@/utils/haptics';
import { sound } from '@/utils/sounds';
import { getChatSettings } from './chatSettings';

function guard(fn: () => void) {
  if (getChatSettings().chatVibration) fn();
}

export const chatHaptic = {
  /** Send message — light pop + pop sound */
  send: () => { guard(() => haptic.light()); sound.pop(); },
  /** Receive message — soft double-pulse nudge */
  receive: () => guard(() => haptic.nudge()),
  /** Long-press — heavy impact */
  longPress: () => guard(() => haptic.heavy()),
  /** Emoji/reaction tap — light pop + pop sound */
  reaction: () => { guard(() => haptic.light()); sound.pop(); },
  /** Swipe-to-reply: threshold cross tick */
  swipeProgress: () => guard(() => haptic.tick()),
  /** Swipe-to-reply: past midpoint */
  swipeThreshold: () => guard(() => haptic.buzz()),
  /** Swipe-to-reply: completed */
  swipeComplete: () => guard(() => haptic.success()),
  /** Voice play/pause */
  voiceTap: () => guard(() => haptic.tick()),
  /** Message action — distinct per type */
  action: (type: 'delete' | 'star' | 'pin' | 'forward' | 'edit' | 'copy') => {
    guard(() => type === 'delete' ? haptic.error() : haptic.light());
  },
  /** Typing indicator appears */
  typing: () => guard(() => haptic.tick()),
  /** Poll vote cast */
  pollVote: () => guard(() => haptic.success()),
  /** Media viewer — double-tap zoom */
  mediaZoom: () => guard(() => haptic.light()),
  /** Media viewer — swipe dismiss */
  mediaDismiss: () => guard(() => haptic.buzz()),
  /** Media viewer — pinch zoom threshold */
  mediaPinch: () => guard(() => haptic.tick()),
};
